import { defaultConditionValue } from './conditionExpression';
import {
  conditionOperatorAvailable,
  NUMERIC_COMPARISON_OPERATORS as numericComparisonOperators,
  REGEX_OPERATORS as regexOperators,
} from './ruleCapabilities';

export const multipleOperators = new Set([
  'in',
  'not_in',
  'contains_any',
  'contains_all',
  'contains_none',
  'text_contains_any',
  'text_not_contains_any',
  'text_contains_any_case',
]);
export const numericOperators = ['eq', 'gt', 'gte', 'lt', 'lte', 'between'];
export const descriptorForCapabilities = (descriptor, advancedConditions) => ({
  ...descriptor,
  operators: (descriptor?.operators || []).filter(operator =>
    conditionOperatorAvailable(operator, descriptor, advancedConditions)
  ),
});
export const isNumericCondition = (condition, descriptor) =>
  descriptor?.value_type === 'number' ||
  numericComparisonOperators.has(condition.operator);
export const preferredOperator = descriptor =>
  (descriptor?.value_type === 'number' &&
  descriptor?.operators?.includes('between')
    ? 'between'
    : null) ||
  descriptor?.operators?.find(
    operator => operator === 'in' || operator === 'contains_any'
  ) ||
  descriptor?.operators?.[0];

const fieldLabels = {
  available: 'Availability',
  brand_id: 'Brand',
  category_ancestor_ids: 'Category subtree',
  category_ids: 'Category',
  category_path_names: 'Category path names',
  category_path_text: 'Category path text',
  group_id: 'Product group',
  normalized_product_name: 'Product type and name',
  primary_category_name: 'Primary category name',
  product_name: 'Product name',
  product_type: 'Product Type',
  vendor_name: 'Vendor name',
  price: 'Price',
  primary_category_id: 'Primary category',
  product_id: 'Product',
  stock_quantity: 'Stock quantity',
};
export const humanField = (field, descriptor = null) =>
  descriptor?.label ||
  descriptor?.value_source?.label ||
  fieldLabels[field] ||
  field
    .replace(/^attributes\.feature[:.]?/, 'Feature #')
    .replace(/^attributes\./, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
export const descriptorForSavedCondition = (
  condition,
  schema,
  advancedConditions = true
) => {
  const savedAttributeLabel = condition.field.startsWith('attributes.')
    ? condition.field.slice('attributes.'.length)
    : null;
  const labelMatches = savedAttributeLabel
    ? Object.values(schema).filter(
        candidate =>
          candidate?.key?.startsWith('attributes.') &&
          [candidate.label, candidate.value_source?.label].includes(
            savedAttributeLabel
          )
      )
    : [];
  // A translated display label is only a safe legacy identity when unique.
  // Never let object iteration order silently retarget an imported rule.
  const descriptor =
    schema[condition.field] ||
    (labelMatches.length === 1 ? labelMatches[0] : null);
  if (descriptor) {
    const supported = (descriptor.operators || []).filter(operator =>
      conditionOperatorAvailable(operator, descriptor, advancedConditions)
    );
    if (
      supported.length === (descriptor.operators || []).length &&
      supported.includes(condition.operator)
    )
      return descriptor;
    return supported.includes(condition.operator)
      ? { ...descriptor, operators: supported }
      : { ...descriptor, operators: [condition.operator, ...supported] };
  }
  // Saved rules can target catalog attributes that are not configured as facets.
  const numeric = numericComparisonOperators.has(condition.operator);
  return {
    label: humanField(condition.field),
    value_type: 'scalar_or_list',
    operators:
      advancedConditions && numeric && condition.field.startsWith('attributes.')
        ? numericOperators.includes(condition.operator)
          ? numericOperators
          : [condition.operator, ...numericOperators]
        : [condition.operator],
  };
};
export const canonicalizeSavedConditionFields = (group, schema) => ({
  ...group,
  items: (group?.items || []).map(item => {
    if (Array.isArray(item.items))
      return canonicalizeSavedConditionFields(item, schema);
    const descriptor = descriptorForSavedCondition(item, schema);
    return descriptor?.key && descriptor.key !== item.field
      ? { ...item, field: descriptor.key }
      : item;
  }),
});
const operatorLabels = {
  between: 'is between',
  contains_all: 'contains all',
  contains_any: 'contains one of',
  contains_none: 'contains none',
  text_contains_any: 'contains text',
  text_not_contains_any: 'does not contain text',
  // Keep old rules readable without advertising the implementation-specific legacy mode.
  text_contains_any_case: 'contains text',
  eq: 'is',
  gt: 'is greater than (>)',
  gte: 'is at least (≥)',
  in: 'is one of',
  lt: 'is less than (<)',
  lte: 'is at most (≤)',
  matches_regex: 'matches regular expression',
  neq: 'is not',
  not_in: 'is not one of',
  not_matches_regex: 'does not match regular expression',
};
export const humanOperator = operator =>
  operatorLabels[operator] || operator.replace(/_/g, ' ');
const operatorGroup = operator => {
  if (numericComparisonOperators.has(operator)) return 'number';
  if (regexOperators.has(operator) || operator.startsWith('text_'))
    return 'text';
  if (
    ['in', 'not_in', 'contains_any', 'contains_all', 'contains_none'].includes(
      operator
    )
  ) {
    return 'list';
  }
  return 'value';
};
export const conditionOperatorOptions = (
  descriptor,
  currentOperator,
  t,
  advancedConditions
) =>
  (descriptor?.operators || [])
    .filter(operator => {
      if (
        !conditionOperatorAvailable(operator, descriptor, advancedConditions) &&
        operator !== currentOperator
      )
        return false;
      if (currentOperator === 'text_contains_any_case')
        return operator !== 'text_contains_any';
      return operator !== 'text_contains_any_case';
    })
    .flatMap(operator =>
      operator === 'exists'
        ? [
            {
              value: 'exists:true',
              label: t('merchandising.condition.hasValue'),
              group: 'value',
            },
            {
              value: 'exists:false',
              label: t('merchandising.condition.hasNoValue'),
              group: 'value',
            },
          ]
        : [
            {
              value: operator,
              label: humanOperator(operator),
              group: operatorGroup(operator),
            },
          ]
    );

const operatorGroupOrder = ['value', 'list', 'text', 'number'];
export const groupConditionOperatorOptions = options =>
  operatorGroupOrder
    .map(group => ({
      group,
      options: options.filter(option => option.group === group),
    }))
    .filter(entry => entry.options.length);

export const conditionValueForOperatorChange = (
  condition,
  descriptor,
  selection
) => {
  const operator = selection.startsWith('exists:') ? 'exists' : selection;
  if (operator === 'exists') return selection === 'exists:true';
  if (condition.operator === 'exists')
    return defaultConditionValue(descriptor, operator);

  const wasMultiple = multipleOperators.has(condition.operator);
  const nextMultiple = multipleOperators.has(operator);
  const current = condition.value;
  if (regexOperators.has(operator)) {
    const seed = Array.isArray(current) ? current[0] : current;
    return typeof seed === 'string' ? seed : seed == null ? '' : String(seed);
  }
  if (operator === 'between') {
    if (
      condition.operator === 'between' &&
      Array.isArray(current) &&
      current.length === 2
    ) {
      return current;
    }
    const seed = Array.isArray(current) ? current[0] : current;
    const number = Number(seed);
    return seed === '' ||
      seed == null ||
      typeof seed === 'boolean' ||
      !Number.isFinite(number)
      ? ['', '']
      : [number, number];
  }
  if (numericComparisonOperators.has(operator)) {
    const seed = Array.isArray(current) ? current[0] : current;
    const number = Number(seed);
    return seed === '' ||
      seed == null ||
      typeof seed === 'boolean' ||
      !Number.isFinite(number)
      ? ''
      : number;
  }
  if (condition.operator === 'between') {
    const first = Array.isArray(current) ? current[0] : current;
    if (nextMultiple)
      return first === '' || first == null ? [] : [String(first)];
    return descriptor?.value_type === 'number'
      ? first ?? ''
      : String(first ?? '');
  }
  if (nextMultiple && !wasMultiple) {
    if (current === '' || current == null || typeof current === 'boolean')
      return [];
    return [descriptor?.value_type === 'number' ? current : String(current)];
  }
  if (!nextMultiple && wasMultiple)
    return Array.isArray(current) ? current[0] ?? '' : '';
  if (
    descriptor?.value_type !== 'number' &&
    numericComparisonOperators.has(condition.operator)
  ) {
    return current === '' || current == null ? '' : String(current);
  }
  return current;
};

export const fallbackConditionValueLabel = (id, sourceAction) =>
  sourceAction === 'merchandising.condition_values.search'
    ? String(id)
    : `#${id}`;

export const filterIndexFields = (schema, query) => {
  const normalized = query.trim().toLocaleLowerCase();
  return Object.entries(schema)
    .filter(([, descriptor]) => descriptor.phases?.includes('index'))
    .map(([field, descriptor]) => ({
      field,
      descriptor,
      label: humanField(field, descriptor),
    }))
    .filter(
      option =>
        !normalized ||
        option.label.toLocaleLowerCase().includes(normalized) ||
        option.field.toLocaleLowerCase().includes(normalized)
    )
    .sort((left, right) => left.label.localeCompare(right.label));
};
